/**
 * PATH: packages/ui/src/kanban/components/card-detail/entries/useCardEntries.ts
 * FILE_ID: UI-KANBAN-ENTRIES-HOOK-001
 * PURPOSE: State + mutations for the drawer Entries table — existing
 *          entry sub-cards (subtree query filtered to direct
 *          provider='entry' children) plus draft-row editing, bulk
 *          mint, done/reopen, dismiss (archive) and ←/→ reorder.
 * WHY: ARC-3 (U17). Keeps CardEntriesSection.tsx presentational and
 *      under the 300-line ceiling; every server interaction funnels
 *      through this one hook so invalidation stays consistent.
 * FLOW:
 *   card + api → { entries, drafts, saveDrafts, setDone, dismiss,
 *                  reorder } → section renders rows
 * RELATED:
 *   - entryDraft.ts: pure row transforms
 *   - RestrictedAccessSection.tsx (SubCardsSection): sibling pattern
 *     for the subtree query + invalidation keys reused here
 */

import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, type useQueryClient } from '@tanstack/react-query'
import type { KanbanApi } from '../../../api'
import type { CardEntryRowInput } from '../../../api/payloads-cards'
import type { Card } from '../../../types'
import {
  draftsToRowInputs,
  emptyDraftRow,
  type EntryDraftRow,
} from './entryDraft'

/** Card.provider value stamped on Entries-table sub-cards (BE
 *  `email_kanban/services/card_entries.py::ENTRY_PROVIDER`). */
export const ENTRY_PROVIDER = 'entry'

export interface UseCardEntriesResult {
  /** Live (non-archived) direct entry children, position-ordered. */
  entries: Card[]
  entriesLoading: boolean
  drafts: EntryDraftRow[]
  updateDraft: (key: string, patch: Partial<EntryDraftRow>) => void
  addDraft: () => void
  removeDraft: (key: string) => void
  /** Replace the whole draft set (MR5 paste-from-Excel entry point). */
  setDrafts: (rows: EntryDraftRow[]) => void
  saveDrafts: () => void
  saving: boolean
  /** Row-level or submit-level user-facing error, or null. */
  error: string | null
  setDone: (entry: Card, done: boolean) => void
  /** × — archive the entry sub-card (recoverable, matches spec). */
  dismiss: (entry: Card) => void
  /** ←/→ — swap the entry with its neighbour (dir -1 or +1). */
  reorder: (entry: Card, dir: -1 | 1) => void
  mutating: boolean
}

export function useCardEntries({
  card,
  api,
  qc,
  boardId,
}: {
  card: Card
  api: KanbanApi
  qc: ReturnType<typeof useQueryClient>
  boardId?: string
}): UseCardEntriesResult {
  const [drafts, setDrafts] = useState<EntryDraftRow[]>(() => [emptyDraftRow()])
  const [error, setError] = useState<string | null>(null)

  const hasChildren = (card.child_count ?? 0) > 0
  const subtreeQ = useQuery({
    // Same key as SubCardsSection so the two sections share one fetch.
    queryKey: ['kanban', 'card', card.id, 'subtree'],
    queryFn: () => api.fetchCardSubtree!(card.id),
    enabled: !!api.fetchCardSubtree && hasChildren,
  })

  const entries = useMemo(() => {
    const all = subtreeQ.data ?? []
    return all
      .filter(
        (c) =>
          c.parent_card === card.id
          && c.provider === ENTRY_PROVIDER
          && c.status !== 'ARCHIVED',
      )
      .sort(
        (a, b) =>
          (a.position_in_parent ?? a.position ?? 0)
          - (b.position_in_parent ?? b.position ?? 0),
      )
  }, [subtreeQ.data, card.id])

  const invalidateAll = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id, 'subtree'] })
    void qc.invalidateQueries({
      queryKey: boardId ? ['kanban', 'cards', boardId] : ['kanban', 'cards'],
    })
  }, [boardId, card.id, qc])

  const saveMutation = useMutation({
    mutationFn: (rows: CardEntryRowInput[]) =>
      api.createCardEntries!(card.id, { rows }),
    onSuccess: () => {
      setDrafts([emptyDraftRow()])
      setError(null)
      invalidateAll()
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : 'Save failed'),
  })

  const patchMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.updateCard(id, { status }),
    onSuccess: invalidateAll,
    onError: (err) =>
      setError(err instanceof Error ? err.message : 'Update failed'),
  })

  const reorderMutation = useMutation({
    mutationFn: ({ id, position }: { id: string; position: number }) =>
      api.setCardParent!(id, {
        parent_card_id: card.id,
        position_in_parent: position,
      }),
    onSuccess: invalidateAll,
    onError: (err) =>
      setError(err instanceof Error ? err.message : 'Reorder failed'),
  })

  const updateDraft = useCallback(
    (key: string, patch: Partial<EntryDraftRow>) => {
      setDrafts((rows) =>
        rows.map((r) => (r.key === key ? { ...r, ...patch } : r)),
      )
    },
    [],
  )

  const addDraft = useCallback(
    () => setDrafts((rows) => [...rows, emptyDraftRow()]),
    [],
  )

  const removeDraft = useCallback(
    (key: string) =>
      setDrafts((rows) => {
        const next = rows.filter((r) => r.key !== key)
        return next.length > 0 ? next : [emptyDraftRow()]
      }),
    [],
  )

  const saveDrafts = useCallback(() => {
    const result = draftsToRowInputs(drafts)
    // NOTE: repo tsconfig has strict:false, which disables discriminated-
    // union narrowing on `result.ok` — hence the explicit cast (same
    // workaround as @quotech/quote-builder/grid a1Workbook.ts).
    if (!result.ok) {
      setError((result as { ok: false; error: string }).error)
      return
    }
    setError(null)
    saveMutation.mutate((result as { ok: true; rows: CardEntryRowInput[] }).rows)
  }, [drafts, saveMutation])

  const setDone = useCallback(
    (entry: Card, done: boolean) =>
      patchMutation.mutate({ id: entry.id, status: done ? 'DONE' : 'OPEN' }),
    [patchMutation],
  )

  const dismiss = useCallback(
    (entry: Card) => patchMutation.mutate({ id: entry.id, status: 'ARCHIVED' }),
    [patchMutation],
  )

  const reorder = useCallback(
    (entry: Card, dir: -1 | 1) => {
      const idx = entries.findIndex((e) => e.id === entry.id)
      const target = idx + dir
      if (idx < 0 || target < 0 || target >= entries.length) return
      // Server reindexes siblings around the moved node (tree_helper),
      // so sending the neighbour's slot is a swap.
      reorderMutation.mutate({
        id: entry.id,
        position: entries[target].position_in_parent ?? target,
      })
    },
    [entries, reorderMutation],
  )

  return {
    entries,
    entriesLoading: subtreeQ.isLoading,
    drafts,
    updateDraft,
    addDraft,
    removeDraft,
    setDrafts,
    saveDrafts,
    saving: saveMutation.isPending,
    error,
    setDone,
    dismiss,
    reorder,
    mutating: patchMutation.isPending || reorderMutation.isPending,
  }
}
